/**
 * Pixels publicitaires Meta et Google Ads, chargés seulement après accord.
 *
 * Rien n'est injecté avant que le visiteur ait accepté : ni script, ni
 * requête, ni cookie. C'est la seule lecture correcte de l'article 5(3) de
 * la directive ePrivacy — un pixel chargé « en attendant la réponse » a déjà
 * déposé son cookie, et la bannière ne sert plus à rien.
 *
 * Les identifiants viennent de l'environnement. Tant qu'ils ne sont pas
 * renseignés, ce module ne fait rien du tout : le site reste entièrement
 * sans traceur, et la bannière n'a pas à mentionner la publicité.
 *
 *   REACT_APP_META_PIXEL_ID        ex. 123456789012345
 *   REACT_APP_GOOGLE_ADS_ID        ex. AW-123456789
 *   REACT_APP_GOOGLE_ADS_ACHAT     ex. AW-123456789/AbC-dEfGhIj
 */

const META = process.env.REACT_APP_META_PIXEL_ID || '';
const GOOGLE_ADS = process.env.REACT_APP_GOOGLE_ADS_ID || '';
const GOOGLE_ACHAT = process.env.REACT_APP_GOOGLE_ADS_ACHAT || '';

/** Vrai si au moins un pixel est configuré : sinon, pas de rubrique « publicité ». */
export const publiciteConfiguree = Boolean(META || GOOGLE_ADS);

let metaCharge = false;
let googleCharge = false;
let autorise = false;

function gtag(...args) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

/**
 * Déclare le refus par défaut, avant tout chargement.
 *
 * Le Consent Mode v2 est exigé par Google depuis mars 2024 pour diffuser des
 * annonces personnalisées dans l'Espace économique européen. Ces appels ne
 * chargent aucun script et ne déposent rien : ils empilent une instruction
 * que la balise lira si elle est chargée plus tard.
 */
export function declarerRefusParDefaut() {
  if (!GOOGLE_ADS) return;
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
}

function chargerMeta() {
  if (metaCharge || !META || window.fbq) return;
  metaCharge = true;
  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = true; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', META);
  window.fbq('track', 'PageView');
}

function chargerGoogle() {
  if (googleCharge || !GOOGLE_ADS) return;
  googleCharge = true;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS}`;
  document.head.appendChild(script);
  gtag('js', new Date());
  gtag('config', GOOGLE_ADS);
}

/** Supprime les cookies déposés par les pixels, au retrait du consentement. */
function effacerCookiesPublicitaires() {
  const prefixes = ['_fbp', '_fbc', '_gcl_au', '_gcl_aw', '_gcl_dc', '_gac_'];
  const hote = window.location.hostname;
  // Le cookie a pu être posé sur le domaine ou sur le domaine parent : on
  // tente les deux, un effacement sur le mauvais domaine reste sans effet.
  const domaines = [hote, `.${hote}`, `.${hote.split('.').slice(-2).join('.')}`];
  document.cookie.split(';').forEach((brut) => {
    const nom = brut.split('=')[0].trim();
    if (!prefixes.some((p) => nom.startsWith(p))) return;
    domaines.forEach((d) => {
      document.cookie = `${nom}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${d}`;
    });
    document.cookie = `${nom}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  });
}

/** Applique le choix du visiteur. Appelé à chaque changement. */
export function appliquerConsentementPublicite(accepte) {
  autorise = accepte;
  if (accepte) {
    if (GOOGLE_ADS) {
      gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      });
    }
    chargerMeta();
    chargerGoogle();
    return;
  }
  // Un script déjà chargé ne peut pas être retiré, mais on cesse de lui
  // parler, on repasse le Consent Mode en refus, et on efface ses cookies.
  if (GOOGLE_ADS && googleCharge) {
    gtag('consent', 'update', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });
  }
  effacerCookiesPublicitaires();
}

// ── Événements ────────────────────────────────────────────────────────────
// Chacun vérifie l'autorisation : un appel passé sans accord ne fait rien.

export function pixelFicheVue({ id, nom, prix }) {
  if (!autorise) return;
  if (window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_ids: [id], content_name: nom, content_type: 'product',
      value: prix, currency: 'EUR',
    });
  }
  if (googleCharge) {
    gtag('event', 'view_item', { currency: 'EUR', value: prix, items: [{ item_id: id, item_name: nom }] });
  }
}

export function pixelAjoutPanier({ id, nom, prix, quantite = 1 }) {
  if (!autorise) return;
  if (window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_ids: [id], content_name: nom, content_type: 'product',
      value: prix * quantite, currency: 'EUR',
    });
  }
  if (googleCharge) {
    gtag('event', 'add_to_cart', {
      currency: 'EUR', value: prix * quantite,
      items: [{ item_id: id, item_name: nom, quantity: quantite }],
    });
  }
}

export function pixelTunnel({ total, nombreArticles }) {
  if (!autorise) return;
  if (window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      value: total, currency: 'EUR', num_items: nombreArticles,
    });
  }
  if (googleCharge) {
    gtag('event', 'begin_checkout', { currency: 'EUR', value: total });
  }
}

/** L'achat : le seul événement qui compte vraiment pour juger une campagne. */
export function pixelAchat({ commandeId, total }) {
  if (!autorise) return;
  if (window.fbq) {
    window.fbq('track', 'Purchase', {
      value: total, currency: 'EUR', eventID: commandeId,
    });
  }
  if (googleCharge && GOOGLE_ACHAT) {
    gtag('event', 'conversion', {
      send_to: GOOGLE_ACHAT, value: total, currency: 'EUR',
      transaction_id: commandeId,
    });
  }
}
